import "react-datepicker/dist/react-datepicker.css";
import { DropdownSelector } from "@/components/dropdown/DropDown";
import { Button } from "antd";
import { IMajorProjectsFilter } from "@/types/majorProjects";
import React, { useEffect, useState } from "react";
import { useShallow } from "zustand/shallow";
import { useMajorProjectStore } from "@/stores/majorProjectStore";
import { TEXT_APPLY, TEXT_CLEAR, TEXT_COMPANY, TEXT_PROJECTNAME } from "@/constants/strings";
import { THEME } from "@/constants";
import { getFiltersByIds } from "@/utils/filtersUtil";

const FilterMajorProjects: React.FC<IMajorProjectsFilter> = ({
  handleFilterData,
  filters,
  tempFilters,
  selectedCompany,
  setSelectedCompany,
  selectedProject,
  setSelectedProject,
  setTempFilters,
  setSearchInputValue
}) => {
  const [stateSelectedCompanies, setStateSelectedCompanies] = useState<string[]>(selectedCompany);
  const [stateSelectedProjects, setStateSelectedProjects] = useState<string[]>(selectedProject);
  const { setNameIn } = useMajorProjectStore(
    useShallow(state => ({
      setNameIn: state.setNameIn
    }))
  );
  const handleSelectCompany = (selectedIds: string[]) => {
    const allProjectNames = filters.projectNames || [];

    let filteredProjectNames;

    if (selectedIds.length === 0) {
      filteredProjectNames = allProjectNames;
      setStateSelectedProjects([]);
    } else {
      filteredProjectNames = getFiltersByIds(selectedIds, allProjectNames, true);
      setStateSelectedProjects(filteredProjectNames.map(project => project.id));
    }
    setStateSelectedCompanies(selectedIds);
  };

  const handleSelectProject = (selectedIds: string[]) => {
    const allProjects = filters.projectNames || [];
    const allCompanies = filters.companies || [];

    if (selectedIds.length === 0) {
      setStateSelectedProjects([]);
      setStateSelectedCompanies([]);
      return;
    }

    const selectedProjects = allProjects.filter(project => selectedIds.includes(project.id));
    const companyIds = selectedProjects.flatMap(project => project.companyIds || []);

    // Filter companies to only those that match any selected project's company
    const filteredCompanies = getFiltersByIds(companyIds, allCompanies, false);

    setStateSelectedProjects(selectedIds);
    setStateSelectedCompanies(filteredCompanies.map(company => company.id));
  };

  const initializeFilters = () => {
    const defaultCompanyIds = filters.companies.map(company => company.id);
    const defaultProjectIds = filters.projectNames.map(project => project.id);

    setStateSelectedCompanies(defaultCompanyIds);
    setStateSelectedProjects(defaultProjectIds);

    setSelectedCompany(defaultCompanyIds);
    setSelectedProject(defaultProjectIds);

    setTempFilters({
      companies: filters.companies,
      projectNames: filters.projectNames
    });

    setNameIn(filters.projectNames.map(p => p.displayName));
    handleFilterData();
  };

  useEffect(() => {
    const isResetTriggered =
      selectedCompany.length === 0 && selectedProject.length === 0 && stateSelectedCompanies.length > 0 && stateSelectedProjects.length > 0;

    if (isResetTriggered) {
      initializeFilters();
    }
  }, [selectedCompany, selectedProject, filters]);

  useEffect(() => {
    setStateSelectedCompanies(selectedCompany);
  }, [selectedCompany]);

  useEffect(() => {
    setStateSelectedProjects(selectedProject);
  }, [selectedProject]);

  return (
    <div className="wrapper w-full inline-flex my-5 gap-3 bg-[#FAFAFA] rounded-md border border-gray-200">
      <div className="content=wrapper w-full m-3 z-50">
        <div className="first-line-filter gap-3 grid grid-cols-3">
          <div className="flex-1/3">
            <DropdownSelector
              title={TEXT_COMPANY}
              options={filters.companies}
              selected={stateSelectedCompanies}
              onSelect={ids => handleSelectCompany(ids)}
            />
          </div>
          <div className="flex-1/3">
            <DropdownSelector
              title={TEXT_PROJECTNAME}
              options={tempFilters.projectNames}
              selected={stateSelectedProjects}
              onSelect={selectedProject => handleSelectProject(selectedProject)}
            />
          </div>
          <div className="button-combination flex-1/5">
            <div className="w-1/2 flex gap-2 items-center h-full">
              <Button
                className="!text-gray-900 !rounded-md !border !border-gray-300 !h-full !cursor-pointer !font-semibold"
                onClick={() => {
                  setNameIn([]);
                  setSearchInputValue("");
                  initializeFilters();
                }}
              >
                {TEXT_CLEAR}
              </Button>
              <Button
                className="!rounded-md !border !border-gray-300 !h-full !cursor-pointer !font-semibold"
                style={{ color: THEME.COLORS.ORANGE_600 }}
                onClick={() => {
                  setSelectedCompany(stateSelectedCompanies);
                  setSelectedProject(stateSelectedProjects);

                  // Calculate display names based on current temp selections
                  const allProjects = filters.projectNames || [];
                  const selectedProjects =
                    stateSelectedProjects.length > 0 ? allProjects.filter(project => stateSelectedProjects.includes(project.id)) : allProjects;
                  setNameIn(selectedProjects.map(project => project.displayName));

                  handleFilterData();
                }}
              >
                {TEXT_APPLY}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterMajorProjects;
